import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Calendar, FileText, Settings, Plus, Save, Trash2, Edit2, X, CheckCircle2, BookOpen, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Quote, Code, Image, Link2, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, FileCode, Eye, Lock, Globe, Upload as UploadIcon } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExt from '@tiptap/extension-image';
import LinkExt from '@tiptap/extension-link';
import UnderlineExt from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Node, mergeAttributes } from '@tiptap/core';

// Custom TipTap node for iframe embedding (PDF, Office docs)
const IframeNode = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      style: { default: 'width:100%;height:600px;border:1px solid #262626;background:#fff' },
      title: { default: '' },
      frameborder: { default: '0' },
    };
  },
  parseHTML() { return [{ tag: 'iframe' }]; },
  renderHTML({ HTMLAttributes }) { return ['iframe', mergeAttributes(HTMLAttributes)]; },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:relative;margin:12px 0;';
      wrapper.className = 'embed-wrapper';

      const delBtn = document.createElement('button');
      delBtn.innerHTML = '&times; Remove';
      delBtn.style.cssText = 'position:absolute;top:8px;right:8px;z-index:10;background:#CC0000;color:#fff;border:none;padding:4px 10px;cursor:pointer;font-size:12px;font-weight:bold;opacity:0;transition:opacity 0.2s;';
      delBtn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); const pos = getPos(); editor.chain().focus().deleteRange({ from: pos, to: pos + 1 }).run(); };

      const label = document.createElement('div');
      label.style.cssText = 'padding:8px 12px;background:#0A0A0A;border:1px solid #262626;border-bottom:0;color:#fff;font-size:12px;font-weight:bold;';
      label.textContent = 'Document: ' + (node.attrs.title || 'Embedded');

      const iframe = document.createElement('iframe');
      iframe.src = node.attrs.src;
      iframe.style.cssText = node.attrs.style || 'width:100%;height:600px;border:1px solid #262626;background:#fff';
      iframe.title = node.attrs.title || '';
      iframe.frameBorder = '0';

      wrapper.onmouseenter = () => { delBtn.style.opacity = '1'; wrapper.style.outline = '2px solid #CC0000'; };
      wrapper.onmouseleave = () => { delBtn.style.opacity = '0'; wrapper.style.outline = 'none'; };

      wrapper.appendChild(delBtn);
      wrapper.appendChild(label);
      wrapper.appendChild(iframe);
      return { dom: wrapper };
    };
  },
});

// Custom node for video
const VideoNode = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
      style: { default: 'max-width:100%;border:1px solid #262626' },
    };
  },
  parseHTML() { return [{ tag: 'video' }]; },
  renderHTML({ HTMLAttributes }) { return ['video', mergeAttributes({ ...HTMLAttributes, controls: '' }), 0]; },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:relative;margin:12px 0;display:inline-block;max-width:100%;';
      wrapper.className = 'embed-wrapper';

      const delBtn = document.createElement('button');
      delBtn.innerHTML = '&times; Remove';
      delBtn.style.cssText = 'position:absolute;top:8px;right:8px;z-index:10;background:#CC0000;color:#fff;border:none;padding:4px 10px;cursor:pointer;font-size:12px;font-weight:bold;opacity:0;transition:opacity 0.2s;';
      delBtn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); const pos = getPos(); editor.chain().focus().deleteRange({ from: pos, to: pos + 1 }).run(); };

      const video = document.createElement('video');
      video.src = node.attrs.src;
      video.controls = true;
      video.style.cssText = node.attrs.style || 'max-width:100%;border:1px solid #262626';

      wrapper.onmouseenter = () => { delBtn.style.opacity = '1'; wrapper.style.outline = '2px solid #CC0000'; };
      wrapper.onmouseleave = () => { delBtn.style.opacity = '0'; wrapper.style.outline = 'none'; };

      wrapper.appendChild(delBtn);
      wrapper.appendChild(video);
      return { dom: wrapper };
    };
  },
});

// Custom node for audio
const AudioNode = Node.create({
  name: 'audio',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
    };
  },
  parseHTML() { return [{ tag: 'audio' }]; },
  renderHTML({ HTMLAttributes }) { return ['audio', mergeAttributes({ ...HTMLAttributes, controls: '' }), 0]; },
  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:relative;margin:12px 0;';
      wrapper.className = 'embed-wrapper';

      const delBtn = document.createElement('button');
      delBtn.innerHTML = '&times; Remove';
      delBtn.style.cssText = 'position:absolute;top:-4px;right:0;z-index:10;background:#CC0000;color:#fff;border:none;padding:4px 10px;cursor:pointer;font-size:12px;font-weight:bold;opacity:0;transition:opacity 0.2s;';
      delBtn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); const pos = getPos(); editor.chain().focus().deleteRange({ from: pos, to: pos + 1 }).run(); };

      const audio = document.createElement('audio');
      audio.src = node.attrs.src;
      audio.controls = true;
      audio.style.cssText = 'width:100%';

      wrapper.onmouseenter = () => { delBtn.style.opacity = '1'; wrapper.style.outline = '2px solid #CC0000'; };
      wrapper.onmouseleave = () => { delBtn.style.opacity = '0'; wrapper.style.outline = 'none'; };

      wrapper.appendChild(delBtn);
      wrapper.appendChild(audio);
      return { dom: wrapper };
    };
  },
});

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExt,
      ImageExt.configure({ inline: true, allowBase64: true }),
      LinkExt.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      IframeNode,
      VideoNode,
      AudioNode,
    ],
    content: value || '',
    onUpdate: ({ editor }) => { onChange(editor.getHTML()); },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value]);

  if (!editor) return null;

  const addImage = () => {
    const url = window.prompt('Image URL (or use upload button below):');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };
  const addLink = () => {
    const url = window.prompt('Link URL:');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };
  const insertMedia = (url, filename, type) => {
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    if (type && type.startsWith('image/')) {
      editor.chain().focus().setImage({ src: fullUrl, alt: filename }).run();
    } else if (type && type.startsWith('audio/')) {
      editor.chain().focus().insertContent({ type: 'audio', attrs: { src: fullUrl, controls: true } }).run();
    } else if (type && type.startsWith('video/')) {
      editor.chain().focus().insertContent({ type: 'video', attrs: { src: fullUrl, controls: true, style: 'max-width:100%;border:1px solid #262626' } }).run();
    } else {
      const ext = filename.split('.').pop().toLowerCase();
      const isPdf = ext === 'pdf';
      const isOffice = ['docx', 'doc', 'pptx', 'ppt', 'xlsx', 'xls'].includes(ext);
      
      if (isPdf) {
        // PDF — embed directly via iframe
        editor.chain().focus()
          .insertContent({ type: 'iframe', attrs: { src: fullUrl, title: filename, style: 'width:100%;height:600px;border:1px solid #262626;background:#fff' } })
          .run();
      } else if (isOffice) {
        // Office docs — use Microsoft Office Online viewer
        const msViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fullUrl)}`;
        editor.chain().focus()
          .insertContent({ type: 'iframe', attrs: { src: msViewerUrl, title: filename, style: 'width:100%;height:600px;border:1px solid #262626;background:#fff' } })
          .run();
      } else {
        editor.chain().focus().insertContent(`<p><a href="${fullUrl}" target="_blank">${filename}</a></p>`).run();
      }
    }
  };

  const Btn = ({ onClick, active, children, title }) => (
    <button type="button" onClick={onClick} title={title} className={`p-1.5 transition-colors ${active ? 'text-[#F59E0B] bg-[#F59E0B]/15' : 'text-zinc-400 hover:text-white'}`}>{children}</button>
  );

  return (
    <div className="border border-[#262626]">
      <div className="flex flex-wrap gap-0.5 p-2 bg-[#111111] border-b border-[#262626]">
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1"><Heading1 className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2"><Heading2 className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#262626] mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline"><UnderlineIcon className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#262626] mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered List"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote"><Quote className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive('codeBlock')} title="Code Block"><Code className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#262626] mx-1" />
        <Btn onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left"><AlignLeft className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center"><AlignCenter className="w-4 h-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right"><AlignRight className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#262626] mx-1" />
        <Btn onClick={addLink} active={editor.isActive('link')} title="Add Link"><Link2 className="w-4 h-4" /></Btn>
        <Btn onClick={addImage} title="Add Image URL"><Image className="w-4 h-4" /></Btn>
        <span className="w-px bg-[#262626] mx-1" />
        <FileUploadButton accept="image/*" label="Upload Image" onUpload={insertMedia} />
        <FileUploadButton accept="audio/*" label="Upload Audio" onUpload={insertMedia} />
        <FileUploadButton accept="video/*" label="Upload Video" onUpload={insertMedia} />
        <FileUploadButton accept=".pdf,.doc,.docx,.pptx,.xlsx,.csv,.txt" label="Upload Doc" onUpload={insertMedia} />
      </div>
      <EditorContent editor={editor} className="tiptap-editor" />
    </div>
  );
}

function EventEditor() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', date: '', time: '', location: '', event_type: 'meetup', max_attendees: 50 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { fetchEvents(); }, []);
  const fetchEvents = () => axios.get(`${API}/events`).then(r => { if (Array.isArray(r.data)) setEvents(r.data); }).catch(() => {});

  const saveEvent = async () => {
    setSaving(true); setMsg('');
    try {
      if (editing) {
        await axios.put(`${API}/events/${editing}`, form, { withCredentials: true });
        setMsg('Event updated!');
      } else {
        await axios.post(`${API}/events`, form, { withCredentials: true });
        setMsg('Event created!');
      }
      setEditing(null); setCreating(false);
      setForm({ title: '', description: '', date: '', time: '', location: '', event_type: 'meetup', max_attendees: 50 });
      fetchEvents();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${id}`, { withCredentials: true });
      fetchEvents(); setMsg('Event deleted');
      setTimeout(() => setMsg(''), 3000);
    } catch {}
  };

  const startEdit = (e) => {
    setEditing(e.id);
    setCreating(true);
    setForm({ title: e.title, description: e.description, date: e.date, time: e.time, location: e.location, event_type: e.event_type || 'meetup', max_attendees: e.max_attendees || 50 });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Events & Meetups</h3>
        <button onClick={() => { setCreating(!creating); setEditing(null); setForm({ title: '', description: '', date: '', time: '', location: '', event_type: 'meetup', max_attendees: 50 }); }} className="brand-btn text-sm px-4 py-2 flex items-center gap-2">
          {creating ? <><X className="w-4 h-4" /> Cancel</> : <><Plus className="w-4 h-4" /> New Event</>}
        </button>
      </div>
      {msg && <div className="mb-4 p-3 bg-green-900/20 border border-green-600 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}

      {creating && (
        <div className="border border-[#262626] bg-[#0A0A0A] p-6 mb-6 space-y-4">
          <p className="text-base font-semibold text-white">{editing ? 'Edit Event' : 'New Event'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Title</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="Event title" />
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Type</label>
              <select value={form.event_type} onChange={e => setForm({...form, event_type: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]">
                <option value="meetup">Meetup</option>
                <option value="workshop">Workshop</option>
                <option value="info_session">Info Session</option>
                <option value="webinar">Webinar</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" />
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Time</label>
              <input value={form.time} onChange={e => setForm({...form, time: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="e.g., 6:00 PM EST" />
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Location / Zoom Link</label>
              <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="Virtual (Zoom) or address" />
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Max Attendees</label>
              <input type="number" value={form.max_attendees} onChange={e => setForm({...form, max_attendees: parseInt(e.target.value) || 50})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" />
            </div>
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6] resize-none" placeholder="Event description..." />
          </div>
          <button onClick={saveEvent} disabled={saving || !form.title || !form.date} className="brand-btn text-sm px-6 py-2 flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : editing ? 'Update Event' : 'Create Event'}
          </button>
        </div>
      )}

      <div className="space-y-0 border border-[#262626]">
        {events.map((e, i) => (
          <div key={e.id || i} className="flex items-center justify-between p-4 border-b border-[#262626] last:border-b-0 bg-[#111111]">
            <div>
              <p className="text-base font-medium text-white">{e.title}</p>
              <p className="text-sm text-zinc-300">{e.date} &middot; {e.time} &middot; {e.location}</p>
              <p className="text-xs text-zinc-400 mt-1">{e.rsvp_count || 0} RSVPs / {e.max_attendees} max</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(e)} data-testid={`edit-event-${i}`} className="text-sm text-[#0077B6] border border-[#0077B6] px-3 py-1.5 hover:bg-[#0077B6] hover:text-white transition-colors flex items-center gap-1"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => deleteEvent(e.id)} data-testid={`delete-event-${i}`} className="text-sm text-[#F59E0B] border border-[#CC0000] px-3 py-1.5 hover:bg-[#CC0000] hover:text-white transition-colors flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        ))}
        {events.length === 0 && <div className="p-8 text-center text-zinc-400">No events yet</div>}
      </div>
    </div>
  );
}

function BlogEditor() {
  const [posts, setPosts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [viewMode, setViewMode] = useState('published'); // 'published' | 'drafts'
  const [expandedDraft, setExpandedDraft] = useState(null);
  const [generatingToday, setGeneratingToday] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', tags: [] });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { fetchPosts(); fetchDrafts(); }, []);
  const fetchPosts = () => axios.get(`${API}/blog`).then(r => { if (Array.isArray(r.data)) setPosts(r.data); }).catch(() => {});
  const fetchDrafts = () => axios.get(`${API}/admin/blog/drafts`, { withCredentials: true }).then(r => { if (Array.isArray(r.data)) setDrafts(r.data); }).catch(() => {});

  const approveDraft = async (slug) => {
    try {
      await axios.post(`${API}/admin/blog/drafts/${slug}/approve`, {}, { withCredentials: true });
      setMsg('✓ Published to public blog');
      fetchDrafts(); fetchPosts();
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Approve failed');
    }
  };

  const rejectDraft = async (slug) => {
    if (!window.confirm('Delete this draft permanently?')) return;
    try {
      await axios.delete(`${API}/admin/blog/drafts/${slug}`, { withCredentials: true });
      setMsg('Draft deleted');
      fetchDrafts();
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Delete failed');
    }
  };

  const editDraft = (d) => {
    // Load a draft into the editor for tweaking; saving will preserve it as a draft
    setEditing(d.slug);
    setCreating(true);
    setViewMode('published');
    setForm({ title: d.title || '', slug: d.slug || '', excerpt: d.excerpt || '', content: d.content || '', tags: d.tags || [] });
  };

  const generateToday = async () => {
    setGeneratingToday(true); setMsg('');
    try {
      await axios.post(`${API}/admin/generate-daily-posts`, {}, { withCredentials: true });
      setMsg("Draft generation started — takes ~30 seconds. Refreshing…");
      setTimeout(() => { fetchDrafts(); setMsg(''); }, 35000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Generation failed');
    }
    setGeneratingToday(false);
  };

  const suggestBlogTags = async () => {
    const plainText = `${form.title || ''}\n${form.excerpt || ''}\n\n${(form.content || '').replace(/<[^>]+>/g, ' ')}`.trim();
    if (plainText.length < 40) { setMsg('Write at least a title + a few lines of content before auto-suggesting tags'); setTimeout(() => setMsg(''), 3000); return; }
    setSuggesting(true); setMsg('');
    try {
      const fd = new FormData();
      fd.append('text', plainText);
      fd.append('count', '7');
      const { data } = await axios.post(`${API}/admin/suggest-tags`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const merged = [...form.tags];
      (data.tags || []).forEach(t => {
        if (!merged.some(x => x.toLowerCase() === t.toLowerCase()) && merged.length < 10) merged.push(t);
      });
      setForm({ ...form, tags: merged });
      setMsg(`Added ${merged.length - form.tags.length} suggested tags`);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Tag suggestion failed');
    }
    setSuggesting(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const savePost = async () => {
    setSaving(true); setMsg('');
    try {
      if (editing) {
        await axios.put(`${API}/blog/${editing}`, form, { withCredentials: true });
        setMsg('Post updated!');
      } else {
        await axios.post(`${API}/blog`, form, { withCredentials: true });
        setMsg('Post created!');
      }
      setEditing(null); setCreating(false);
      setForm({ title: '', slug: '', excerpt: '', content: '', tags: [] });
      fetchPosts();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const deletePost = async (slug) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/${slug}`, { withCredentials: true });
      fetchPosts(); setMsg('Post deleted');
      setTimeout(() => setMsg(''), 3000);
    } catch {}
  };

  const startEdit = (p) => {
    setEditing(p.slug);
    setCreating(true);
    setForm({ title: p.title, slug: p.slug, excerpt: p.excerpt, content: p.content, tags: p.tags || [] });
  };

  const autoSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Blog Posts</h3>
          <div className="flex gap-0 border border-[#262626] ml-4">
            <button onClick={() => setViewMode('published')} data-testid="blog-view-published" className={`px-3 py-1 text-xs font-mono uppercase tracking-wider ${viewMode === 'published' ? 'bg-[#0077B6] text-white' : 'text-zinc-400 hover:text-white'}`}>Published ({posts.length})</button>
            <button onClick={() => setViewMode('drafts')} data-testid="blog-view-drafts" className={`px-3 py-1 text-xs font-mono uppercase tracking-wider ${viewMode === 'drafts' ? 'bg-[#F59E0B] text-black' : 'text-zinc-400 hover:text-white'}`}>
              Drafts {drafts.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-[#CC0000] text-white font-bold">{drafts.length}</span>}
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          {viewMode === 'drafts' && (
            <button onClick={generateToday} disabled={generatingToday} className="text-xs px-3 py-2 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black transition-colors disabled:opacity-50" data-testid="blog-generate-today-btn">
              {generatingToday ? 'Generating…' : "✨ Generate today's draft"}
            </button>
          )}
          <button onClick={() => { setCreating(!creating); setEditing(null); setForm({ title: '', slug: '', excerpt: '', content: '', tags: [] }); }} className="brand-btn text-sm px-4 py-2 flex items-center gap-2">
            {creating ? <><X className="w-4 h-4" /> Cancel</> : <><Plus className="w-4 h-4" /> New Post</>}
          </button>
        </div>
      </div>
      {msg && <div className="mb-4 p-3 bg-green-900/20 border border-green-600 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}

      {/* Drafts Review panel */}
      {viewMode === 'drafts' && !creating && (
        <div className="mb-6 space-y-3" data-testid="blog-drafts-list">
          {drafts.length === 0 ? (
            <div className="p-10 text-center border border-dashed border-[#262626] text-zinc-400">
              <p className="mb-2">No drafts awaiting review.</p>
              <p className="text-xs text-zinc-500">An AI-generated draft is created automatically once per day. You can also click "✨ Generate today's draft" above.</p>
            </div>
          ) : drafts.map((d, i) => (
            <div key={d.slug} className="border border-[#F59E0B]/30 bg-[#111111]" data-testid={`blog-draft-${i}`}>
              <div className="flex items-start justify-between p-4 gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs font-mono uppercase tracking-widest text-[#F59E0B] px-2 py-0.5 border border-[#F59E0B]/40 bg-[#F59E0B]/10">Draft</span>
                    <span className="text-xs text-zinc-500 font-mono">{new Date(d.created_at).toLocaleString()}</span>
                    {d.category && <span className="text-xs text-zinc-500 font-mono">· {d.category.replace(/_/g, ' ')}</span>}
                  </div>
                  <p className="text-base font-semibold text-white mb-1 cursor-pointer hover:text-[#F59E0B]" onClick={() => setExpandedDraft(expandedDraft === d.slug ? null : d.slug)}>{d.title}</p>
                  <p className="text-sm text-zinc-400 line-clamp-2">{d.excerpt}</p>
                  {d.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {d.tags.slice(0, 8).map((t, j) => <span key={j} className="text-xs px-1.5 py-0.5 bg-[#F59E0B]/10 text-[#F59E0B]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{t}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button onClick={() => approveDraft(d.slug)} className="text-xs px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white font-bold flex items-center gap-1" data-testid={`blog-approve-${i}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Approve & Publish
                  </button>
                  <button onClick={() => editDraft(d)} className="text-xs px-3 py-1.5 border border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white flex items-center gap-1">
                    <Edit2 className="w-3.5 h-3.5" /> Edit First
                  </button>
                  <button onClick={() => rejectDraft(d.slug)} className="text-xs px-3 py-1.5 border border-[#CC0000] text-[#CC0000] hover:bg-[#CC0000] hover:text-white flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              </div>
              {expandedDraft === d.slug && (
                <div className="p-4 border-t border-[#262626] bg-[#0A0A0A] space-y-3">
                  {d.sources?.length > 0 && (
                    <div>
                      <p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">SOURCES CITED</p>
                      <p className="text-xs text-zinc-400">{d.sources.join(' · ')}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">FULL CONTENT</p>
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap max-h-96 overflow-y-auto pr-2 leading-relaxed">{d.content}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <div className="border border-[#262626] bg-[#0A0A0A] p-6 mb-6 space-y-4">
          <p className="text-base font-semibold text-white">{editing ? 'Edit Post' : 'New Post'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Title</label>
              <input value={form.title} onChange={e => { setForm({...form, title: e.target.value, slug: editing ? form.slug : autoSlug(e.target.value)}); }} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="Post title" />
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">URL Slug</label>
              <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="auto-generated-slug" />
            </div>
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Excerpt (short summary)</label>
            <input value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="Brief description for listing..." />
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Content</label>
            <RichTextEditor
              value={form.content}
              onChange={val => setForm({...form, content: val})}
              placeholder="Write your blog post... Use the toolbar to format text, add images, and more."
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-[#F59E0B] font-bold block">Tags (type and press Enter)</label>
              <button
                type="button"
                onClick={suggestBlogTags}
                disabled={suggesting}
                className="text-xs px-3 py-1 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black transition-colors disabled:opacity-50"
                data-testid="blog-suggest-tags-btn"
                title="Analyse title + content to suggest topic tags via AI"
              >
                {suggesting ? 'Analysing…' : '✨ Auto-suggest from content'}
              </button>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {form.tags.map((t, i) => (
                <span key={i} className="text-xs font-mono px-2 py-1 bg-[#F59E0B]/15 text-[#F59E0B] border border-[#CC0000]/20 flex items-center gap-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {t} <button onClick={() => setForm({...form, tags: form.tags.filter((_, j) => j !== i)})} className="hover:text-red-400">&times;</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mb-2">
              <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (tagInput.trim()) { setForm({...form, tags: [...form.tags, tagInput.trim()]}); setTagInput(''); } } }} className="flex-1 px-4 py-2 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6] text-sm" placeholder="Type tag and press Enter" />
            </div>
            {form.tags.length === 0 && (
              <div>
                <p className="text-xs text-zinc-500 mb-1.5">Quick add:</p>
                <div className="flex flex-wrap gap-1">
                  {['AI', 'Machine Learning', 'Data Science', 'Deep Learning', 'NLP', 'Python', 'Tutorial', 'Career Advice', 'Healthcare AI', 'Fintech', 'Data Engineering'].map(tag => (
                    <button key={tag} type="button" onClick={() => setForm({...form, tags: [...form.tags, tag]})} className="text-xs px-2 py-1 bg-[#111111] border border-[#262626] text-zinc-400 hover:text-[#F59E0B] hover:border-[#CC0000] transition-colors">{tag}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <button onClick={savePost} disabled={saving || !form.title || !form.slug} className="brand-btn text-sm px-6 py-2 flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : editing ? 'Update Post' : 'Create Post'}
          </button>
        </div>
      )}

      {viewMode === 'published' && (
        <div className="space-y-0 border border-[#262626]">
          {posts.map((p, i) => (
            <div key={p.slug || i} className="flex items-center justify-between p-4 border-b border-[#262626] last:border-b-0 bg-[#111111]">
              <div>
                <p className="text-base font-medium text-white">{p.title}</p>
                <p className="text-sm text-zinc-300">{p.excerpt?.substring(0, 80)}...</p>
                <div className="flex gap-1 mt-1">{(p.tags||[]).map((t,j) => <span key={j} className="text-xs font-mono px-1 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B]">{t}</span>)}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(p)} data-testid={`edit-post-${i}`} className="text-sm text-[#0077B6] border border-[#0077B6] px-3 py-1.5 hover:bg-[#0077B6] hover:text-white transition-colors flex items-center gap-1"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => deletePost(p.slug)} data-testid={`delete-post-${i}`} className="text-sm text-[#F59E0B] border border-[#CC0000] px-3 py-1.5 hover:bg-[#CC0000] hover:text-white transition-colors flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          ))}
          {posts.length === 0 && <div className="p-8 text-center text-zinc-400">No posts yet</div>}
        </div>
      )}
    </div>
  );
}

function SiteSettingsEditor() {
  const [settings, setSettings] = useState({ graduates_trained: 200, employment_rate: 100, starting_salary: '$80K+', cost_per_lecture_hour: '$24.50' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/site-settings`, { withCredentials: true }).then(r => { if (r.data && !r.data.detail) setSettings(r.data); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, { withCredentials: true });
      setMsg('Settings saved!');
      setTimeout(() => setMsg(''), 3000);
    } catch {}
    setSaving(false);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-[#0077B6] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Homepage Stats & Site Settings</h3>
      {msg && <div className="mb-4 p-3 bg-green-900/20 border border-green-600 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}
      <div className="border border-[#262626] bg-[#0A0A0A] p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Graduates Trained</label>
            <input type="number" value={settings.graduates_trained} onChange={e => setSettings({...settings, graduates_trained: parseInt(e.target.value) || 0})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" />
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Employment Rate (%)</label>
            <input type="number" value={settings.employment_rate} onChange={e => setSettings({...settings, employment_rate: parseInt(e.target.value) || 0})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" />
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Starting Salary Display</label>
            <input value={settings.starting_salary} onChange={e => setSettings({...settings, starting_salary: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="$80K+" />
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Cost per Lecture Hour</label>
            <input value={settings.cost_per_lecture_hour} onChange={e => setSettings({...settings, cost_per_lecture_hour: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="$24.50" />
          </div>
        </div>
        <p className="text-sm text-zinc-400">Note: These settings update the public stats API. The homepage stat cards are rendered from the front-end code. For immediate homepage changes, edit them from the admin's site or redeploy.</p>
        <button onClick={save} disabled={saving} className="brand-btn text-sm px-6 py-2 flex items-center gap-2 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

function CourseEditor() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', price: 595, hours: 20, modules: [], skills: [], video_folder_url: '', video_urls: [] });
  const [moduleInput, setModuleInput] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { axios.get(`${API}/courses`).then(r => { if (Array.isArray(r.data)) setCourses(r.data); }).catch(() => {}); }, []);

  const startEdit = (c) => {
    setEditing(c.slug);
    setForm({ title: c.title, description: c.description, price: c.price, hours: c.hours, modules: c.modules || [], skills: c.skills || [], video_folder_url: c.video_folder_url || '', video_urls: c.video_urls || [] });
  };

  const saveCourse = async () => {
    setSaving(true); setMsg('');
    try {
      await axios.put(`${API}/courses/${editing}`, form, { withCredentials: true });
      setMsg('Course updated!');
      setEditing(null);
      const r = await axios.get(`${API}/courses`);
      setCourses(r.data);
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const updateVideoUrl = (moduleIndex, field, value) => {
    const updated = [...form.video_urls];
    const idx = updated.findIndex(v => v.module_index === moduleIndex);
    if (idx >= 0) {
      updated[idx] = { ...updated[idx], [field]: value };
    } else {
      updated.push({ module_index: moduleIndex, title: '', url: '', filename: '', [field]: value });
    }
    setForm({ ...form, video_urls: updated });
  };

  const getVideoData = (moduleIndex) => {
    return (form.video_urls || []).find(v => v.module_index === moduleIndex) || {};
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-[#0077B6] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Courses</h3>
      {msg && <div className="mb-4 p-3 bg-green-900/20 border border-green-600 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}

      {editing && (
        <div className="border border-[#262626] bg-[#0A0A0A] p-6 mb-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-base font-semibold text-white">Editing: {form.title}</p>
            <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Title</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#F59E0B] font-bold block mb-1">Price ($)</label>
                <input type="number" value={form.price} onChange={e => setForm({...form, price: parseFloat(e.target.value) || 0})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" />
              </div>
              <div>
                <label className="text-sm text-[#F59E0B] font-bold block mb-1">Hours</label>
                <input type="number" value={form.hours} onChange={e => setForm({...form, hours: parseInt(e.target.value) || 0})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" />
              </div>
            </div>
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6] resize-none" />
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Modules (10 weeks)</label>
            <div className="space-y-1 mb-2">
              {form.modules.map((m, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-xs font-mono text-[#F59E0B] w-8 flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{String(i+1).padStart(2,'0')}</span>
                  <input value={m} onChange={e => { const mods = [...form.modules]; mods[i] = e.target.value; setForm({...form, modules: mods}); }} className="flex-1 px-3 py-2 bg-[#111111] border border-[#262626] text-white text-sm outline-none focus:border-[#0077B6]" />
                  <button onClick={() => setForm({...form, modules: form.modules.filter((_, j) => j !== i)})} className="text-[#F59E0B] hover:text-red-400 text-xs">&times;</button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={moduleInput} onChange={e => setModuleInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (moduleInput.trim()) { setForm({...form, modules: [...form.modules, moduleInput.trim()]}); setModuleInput(''); } } }} className="flex-1 px-3 py-2 bg-[#111111] border border-[#262626] text-white text-sm outline-none focus:border-[#0077B6]" placeholder="Add module and press Enter" />
            </div>
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Skills (10 keywords)</label>
            <div className="flex flex-wrap gap-1 mb-2">
              {form.skills.map((s, i) => (
                <span key={i} className="text-xs font-mono px-2 py-1 bg-[#0077B6]/10 text-[#0077B6] flex items-center gap-1">{s} <button onClick={() => setForm({...form, skills: form.skills.filter((_, j) => j !== i)})} className="text-[#F59E0B]">&times;</button></span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={skillInput} onChange={e => setSkillInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (skillInput.trim()) { setForm({...form, skills: [...form.skills, skillInput.trim()]}); setSkillInput(''); } } }} className="flex-1 px-3 py-2 bg-[#111111] border border-[#262626] text-white text-sm outline-none focus:border-[#0077B6]" placeholder="Add skill and press Enter" />
            </div>
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Video Folder URL (OneDrive/SharePoint)</label>
            <input value={form.video_folder_url} onChange={e => setForm({...form, video_folder_url: e.target.value})} className="w-full px-3 py-2 bg-[#111111] border border-[#262626] text-white text-sm outline-none focus:border-[#0077B6]" placeholder="https://onedrive.sharepoint.com/..." />
          </div>
          <div>
            <label className="text-sm text-[#F59E0B] font-bold block mb-1">Video URLs per Module</label>
            <p className="text-xs text-zinc-400 mb-2">Add embed or streaming URLs for each module. Leave empty for placeholder.</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {form.modules.map((mod, i) => {
                const vd = getVideoData(i);
                return (
                  <div key={i} className="flex gap-2 items-center">
                    <span className="text-xs font-mono text-[#F59E0B] w-8 flex-shrink-0" style={{ fontFamily: 'JetBrains Mono, monospace' }}>W{i+1}</span>
                    <input value={vd.url || ''} onChange={e => updateVideoUrl(i, 'url', e.target.value)} className="flex-1 px-2 py-1.5 bg-[#111111] border border-[#262626] text-white text-xs outline-none focus:border-[#0077B6]" placeholder={`Embed URL for: ${mod.substring(0,40)}...`} />
                  </div>
                );
              })}
            </div>
          </div>
          <button onClick={saveCourse} disabled={saving} className="brand-btn text-sm px-6 py-2 flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Course'}
          </button>
        </div>
      )}

      <div className="space-y-0 border border-[#262626]">
        {courses.map((c, i) => (
          <div key={c.slug} className="flex items-center justify-between p-4 border-b border-[#262626] last:border-b-0 bg-[#111111]">
            <div>
              <p className="text-base font-medium text-white">{c.course_number}. {c.title}</p>
              <p className="text-sm text-zinc-300">${c.price} &middot; {c.hours}hrs &middot; {(c.modules||[]).length} modules &middot; {(c.skills||[]).length} skills &middot; {(c.video_urls||[]).filter(v => v.url || v.filename).length} videos</p>
            </div>
            <button onClick={() => startEdit(c)} data-testid={`edit-course-${i}`} className="text-sm text-[#0077B6] border border-[#0077B6] px-3 py-1.5 hover:bg-[#0077B6] hover:text-white transition-colors flex items-center gap-1"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =========== FILE UPLOAD HELPER ===========
function FileUploadButton({ onUpload, accept, label }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      onUpload(data.url, file.name, file.type);
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.detail || err.message)); }
    setUploading(false);
    e.target.value = '';
  };
  return (
    <label className="cursor-pointer text-xs text-[#0077B6] border border-[#0077B6] px-2 py-1 hover:bg-[#0077B6] hover:text-white transition-colors inline-flex items-center gap-1">
      {uploading ? 'Uploading...' : label}
      <input type="file" accept={accept} onChange={handleFile} className="hidden" />
    </label>
  );
}

// =========== GENERIC CRUD EDITOR ===========
function CrudEditor({ title, apiPath, fields, renderItem }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const defaults = {};
  fields.forEach(f => { defaults[f.key] = f.default || ''; });
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { fetchItems(); }, []);
  const fetchItems = () => axios.get(`${API}/${apiPath}`, { withCredentials: true }).then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      if (editing) {
        await axios.put(`${API}/${apiPath}/${editing}`, form, { withCredentials: true });
        setMsg('Updated!');
      } else {
        await axios.post(`${API}/${apiPath}`, form, { withCredentials: true });
        setMsg('Created!');
      }
      setEditing(null); setCreating(false); setForm(defaults); fetchItems();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
    setSaving(false); setTimeout(() => setMsg(''), 3000);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await axios.delete(`${API}/${apiPath}/${id}`, { withCredentials: true }); fetchItems(); setMsg('Deleted'); setTimeout(() => setMsg(''), 3000); } catch {}
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setCreating(true);
    const f = {};
    fields.forEach(field => { f[field.key] = item[field.key] || field.default || ''; });
    setForm(f);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{title}</h3>
        <button onClick={() => { setCreating(!creating); setEditing(null); setForm(defaults); }} className="brand-btn text-sm px-4 py-2 flex items-center gap-2">
          {creating ? <><X className="w-4 h-4" /> Cancel</> : <><Plus className="w-4 h-4" /> New</>}
        </button>
      </div>
      {msg && <div className="mb-4 p-3 bg-green-900/20 border border-green-600 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}

      {creating && (
        <div className="border border-[#262626] bg-[#0A0A0A] p-6 mb-6 space-y-4">
          <p className="text-base font-semibold text-white">{editing ? 'Edit' : 'New'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.filter(f => f.type !== 'textarea' && f.type !== 'richtext' && f.type !== 'list').map(f => (
              <div key={f.key}>
                <label className="text-sm text-[#F59E0B] font-bold block mb-1">{f.label}</label>
                {f.type === 'select' ? (
                  <select value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]">
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder={f.label} />
                )}
              </div>
            ))}
          </div>
          {fields.filter(f => f.type === 'textarea').map(f => (
            <div key={f.key}>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">{f.label}</label>
              <textarea value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})} rows={4} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6] resize-none" placeholder={f.label} />
            </div>
          ))}
          {fields.filter(f => f.type === 'richtext').map(f => (
            <div key={f.key}>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">{f.label}</label>
              <RichTextEditor value={form[f.key]} onChange={val => setForm({...form, [f.key]: val})} />
            </div>
          ))}
          {fields.filter(f => f.type === 'list').map(f => (
            <ListFieldEditor key={f.key} label={f.label} value={form[f.key] || []} onChange={val => setForm({...form, [f.key]: val})} />
          ))}
          <button onClick={save} disabled={saving} className="brand-btn text-sm px-6 py-2 flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </button>
        </div>
      )}

      <div className="space-y-0 border border-[#262626]">
        {items.map((item, i) => (
          <div key={item.id || i} className="flex items-center justify-between p-4 border-b border-[#262626] last:border-b-0 bg-[#111111]">
            <div className="flex-1 min-w-0">{renderItem(item)}</div>
            <div className="flex gap-2 ml-4">
              <button onClick={() => startEdit(item)} className="text-sm text-[#0077B6] border border-[#0077B6] px-3 py-1.5 hover:bg-[#0077B6] hover:text-white transition-colors flex items-center gap-1"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => remove(item.id)} className="text-sm text-[#F59E0B] border border-[#CC0000] px-3 py-1.5 hover:bg-[#CC0000] hover:text-white transition-colors flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-zinc-400">No items yet</div>}
      </div>
    </div>
  );
}

function ListFieldEditor({ label, value, onChange }) {
  const [input, setInput] = useState('');
  return (
    <div>
      <label className="text-sm text-[#F59E0B] font-bold block mb-1">{label}</label>
      <div className="flex flex-wrap gap-1 mb-2">
        {(value || []).map((v, i) => (
          <span key={i} className="text-xs font-mono px-2 py-1 bg-[#0077B6]/10 text-[#0077B6] flex items-center gap-1">{v} <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-[#F59E0B]">&times;</button></span>
        ))}
      </div>
      <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (input.trim()) { onChange([...(value || []), input.trim()]); setInput(''); } } }} className="w-full px-3 py-2 bg-[#111111] border border-[#262626] text-white text-sm outline-none focus:border-[#0077B6]" placeholder="Type and press Enter" />
    </div>
  );
}

// =========== SPECIFIC CMS EDITORS ===========

function EmployerPartnersEditor() {
  return <CrudEditor title="Employer Partners" apiPath="admin/employer-partners" fields={[
    { key: 'name', label: 'Company Name', type: 'text' },
    { key: 'location', label: 'Location', type: 'text' },
    { key: 'sector', label: 'Sector / Industry', type: 'text' },
  ]} renderItem={(item) => (
    <div>
      <p className="text-base font-medium text-white">{item.name}</p>
      <p className="text-sm text-zinc-300">{item.location} &middot; {item.sector}</p>
    </div>
  )} />;
}

function TestimonialsEditor() {
  return <CrudEditor title="Testimonials" apiPath="admin/testimonials" fields={[
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'role', label: 'Role', type: 'text' },
    { key: 'company', label: 'Company', type: 'text' },
    { key: 'salary', label: 'Starting Salary', type: 'text' },
    { key: 'quote', label: 'Quote', type: 'textarea' },
  ]} renderItem={(item) => (
    <div>
      <p className="text-base font-medium text-white">"{item.quote?.substring(0, 80)}..."</p>
      <p className="text-sm text-zinc-300">{item.name} &middot; {item.role} at {item.company} &middot; {item.salary}</p>
    </div>
  )} />;
}

function TeamEditor() {
  return <CrudEditor title="Team & Advisory Board" apiPath="admin/team" fields={[
    { key: 'name', label: 'Full Name', type: 'text' },
    { key: 'role', label: 'Title / Role', type: 'text' },
    { key: 'focus', label: 'Focus Area', type: 'text' },
    { key: 'org', label: 'Organization (for advisory)', type: 'text' },
    { key: 'board_type', label: 'Type', type: 'select', options: ['team', 'advisory'], default: 'team' },
  ]} renderItem={(item) => (
    <div>
      <p className="text-base font-medium text-white">{item.name} <span className="text-xs font-mono px-1 py-0.5 bg-[#0077B6]/10 text-[#0077B6] ml-2">{item.board_type}</span></p>
      <p className="text-sm text-zinc-300">{item.role} {item.org ? `at ${item.org}` : ''} {item.focus ? `— ${item.focus}` : ''}</p>
    </div>
  )} />;
}

function IoaCareersEditor() {
  return <CrudEditor title="Institute Careers" apiPath="admin/careers" fields={[
    { key: 'title', label: 'Job Title', type: 'text' },
    { key: 'company', label: 'Company', type: 'text', default: 'CRM Portals LLC (Parent of IoA USA)' },
    { key: 'location', label: 'Location', type: 'text', default: 'Remote / NJ, USA' },
    { key: 'job_type', label: 'Job Type', type: 'select', options: ['full-time', 'part-time', 'contract', 'internship'], default: 'full-time' },
    { key: 'salary_range', label: 'Salary Range', type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'requirements', label: 'Requirements', type: 'list', default: [] },
  ]} renderItem={(item) => (
    <div>
      <p className="text-base font-medium text-white">{item.title}</p>
      <p className="text-sm text-zinc-300">{item.company} &middot; {item.location} &middot; {item.salary_range}</p>
    </div>
  )} />;
}

function CareerSessionsViewer() {
  const [sessions, setSessions] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/career-sessions`, { withCredentials: true })
      .then(r => { if (Array.isArray(r.data)) setSessions(r.data); })
      .catch(() => {});
  }, []);

  const deleteSession = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    try {
      await axios.delete(`${API}/admin/career-sessions/${id}`, { withCredentials: true });
      setSessions(sessions.filter(s => s.id !== id));
      setMsg('Deleted');
      setTimeout(() => setMsg(''), 2000);
    } catch {}
  };

  const q = filter.trim().toLowerCase();
  const filtered = q
    ? sessions.filter(s => {
        const sub = s.submitter || {};
        return [sub.full_name, sub.email, sub.phone, sub.target_job_title, s.resume_filename, s.analysis?.greeting]
          .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
      })
    : sessions;

  const displayName = (s) => {
    const sub = s.submitter || {};
    if (sub.full_name) return sub.full_name;
    if (sub.email) return sub.email;
    if (s.analysis?.greeting) return s.analysis.greeting;
    return s.resume_filename || 'Anonymous Session';
  };

  return (
    <div data-testid="resume-submissions-viewer">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Resume Submissions</h3>
          <p className="text-sm text-zinc-400 mt-0.5">
            {filtered.length}{filter ? ` of ${sessions.length}` : ''} submission{sessions.length === 1 ? '' : 's'} from the Resume Eval / Job Match form
          </p>
        </div>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter by name, email, target role…"
          className="px-3 py-2 bg-[#111111] border border-[#262626] text-white text-sm outline-none focus:border-[#0077B6] w-full sm:w-80"
          data-testid="submissions-filter-input"
        />
      </div>
      {msg && <div className="mb-4 p-3 bg-green-900/20 border border-green-600 text-green-400 text-sm">{msg}</div>}
      <div className="space-y-0 border border-[#262626]">
        {filtered.map((s, idx) => {
          const sub = s.submitter || {};
          const submittedAt = s.created_at ? new Date(s.created_at).toLocaleString() : '';
          return (
            <div key={s.id} className="border-b border-[#262626] last:border-b-0 bg-[#111111]" data-testid={`submission-row-${idx}`}>
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-[#1a1a1a]" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-base font-semibold text-white">{displayName(s)}</p>
                    {sub.email && <span className="text-xs font-mono text-[#0077B6]">{sub.email}</span>}
                    {sub.target_job_title && <span className="text-xs px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">{sub.target_job_title}</span>}
                    {!sub.full_name && !sub.email && <span className="text-xs px-2 py-0.5 bg-zinc-800 text-zinc-500 border border-zinc-700">Pre-form (legacy)</span>}
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">
                    {submittedAt}
                    {sub.phone && <> · 📞 {sub.phone}</>}
                    {sub.address && <> · 📍 {sub.address}</>}
                    {s.resume_filename && <> · 📄 {s.resume_filename}</>}
                  </p>
                </div>
                <div className="flex gap-2 ml-4 items-center">
                  {s.analysis?.overall_score && (
                    <span className="text-xs font-mono px-2 py-1 bg-[#0077B6]/20 text-[#0077B6] border border-[#0077B6]/40" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {s.analysis.overall_score}/100
                    </span>
                  )}
                  {s.resume_url && <a href={s.resume_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] border border-[#0077B6] px-2 py-1 hover:bg-[#0077B6] hover:text-white" onClick={e => e.stopPropagation()}>Resume</a>}
                  {s.audio_url && <a href={s.audio_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0077B6] border border-[#0077B6] px-2 py-1 hover:bg-[#0077B6] hover:text-white" onClick={e => e.stopPropagation()}>Audio</a>}
                  <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }} className="text-xs text-[#F59E0B] border border-[#CC0000] px-2 py-1 hover:bg-[#CC0000] hover:text-white" data-testid={`submission-delete-${idx}`}>Delete</button>
                </div>
              </div>
              {expanded === s.id && (
                <div className="p-4 bg-[#0A0A0A] border-t border-[#262626] space-y-4">
                  {/* Submitter form data block */}
                  <div className="border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-3">
                    <p className="text-xs font-mono text-[#F59E0B] font-bold mb-2 tracking-widest">FORM DATA (Resume Eval / Job Match)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <p className="text-zinc-300"><span className="text-zinc-500">Name:</span> {sub.full_name || '—'}</p>
                      <p className="text-zinc-300"><span className="text-zinc-500">Email:</span> {sub.email || '—'}</p>
                      <p className="text-zinc-300"><span className="text-zinc-500">Phone:</span> {sub.phone || '—'}</p>
                      <p className="text-zinc-300"><span className="text-zinc-500">ZIP:</span> {sub.zip_code || '—'}</p>
                      <p className="text-zinc-300 sm:col-span-2"><span className="text-zinc-500">Address:</span> {sub.address || '—'}</p>
                      <p className="text-zinc-300 sm:col-span-2"><span className="text-zinc-500">Target Role:</span> <span className="text-[#F59E0B] font-semibold">{sub.target_job_title || '—'}</span></p>
                      <p className="text-zinc-300"><span className="text-zinc-500">Salary Range:</span> ${sub.salary_min || '0'} – ${sub.salary_max || '0'}</p>
                      <p className="text-zinc-300"><span className="text-zinc-500">Work Mode:</span> {sub.work_mode || '—'}</p>
                      <p className="text-zinc-300"><span className="text-zinc-500">Radius:</span> {sub.radius || '—'} miles</p>
                      <p className="text-zinc-300"><span className="text-zinc-500">Consent:</span> {sub.consent ? '✓ Yes' : '—'}</p>
                    </div>
                  </div>
                  {/* AI analysis block (existing) */}
                  {s.analysis && (
                    <div className="space-y-3">
                      {s.analysis.summary && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">SUMMARY</p><p className="text-sm text-zinc-300">{s.analysis.summary}</p></div>}
                      {s.analysis.strengths?.length > 0 && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">STRENGTHS</p>{s.analysis.strengths.map((x,i) => <p key={i} className="text-sm text-zinc-300">• {x}</p>)}</div>}
                      {s.analysis.job_matches?.length > 0 && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">JOB MATCHES</p>{s.analysis.job_matches.map((j,i) => <p key={i} className="text-sm text-zinc-300">• {j.title} {j.company ? `at ${j.company}` : ''} — {j.match_pct}% match: {j.reason}</p>)}</div>}
                      {s.analysis.skill_gaps?.length > 0 && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">SKILL GAPS</p>{s.analysis.skill_gaps.map((x,i) => <p key={i} className="text-sm text-zinc-300">• {x}</p>)}</div>}
                      {s.analysis.recommended_courses?.length > 0 && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">RECOMMENDED COURSES</p>{s.analysis.recommended_courses.map((c,i) => <p key={i} className="text-sm text-zinc-300">• {c.title}: {c.reason}</p>)}</div>}
                      {s.analysis.career_advice && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">CAREER ADVICE</p><p className="text-sm text-zinc-300">{s.analysis.career_advice}</p></div>}
                      {s.analysis.emotional_approach && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">EMOTIONAL APPROACH — {s.analysis.emotional_approach.rating}</p><p className="text-sm text-zinc-300 italic mb-2">{s.analysis.emotional_approach.emotional_hook}</p>{s.analysis.emotional_approach.rewrite_suggestions?.map((r,i) => <p key={i} className="text-sm text-zinc-300">• {r}</p>)}</div>}
                      {s.audio_url && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">VOICE ADVICE</p><audio controls src={s.audio_url} className="w-full mt-1" /></div>}
                      {s.resume_text_preview && <div><p className="text-xs font-mono text-[#F59E0B] font-bold mb-1">RESUME PREVIEW</p><p className="text-xs text-zinc-500 whitespace-pre-wrap">{s.resume_text_preview}</p></div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="p-8 text-center text-zinc-400" data-testid="submissions-empty">
            {filter ? `No submissions match "${filter}"` : 'No Resume Eval / Job Match submissions yet. When someone uploads a resume on /career-advisor, their full form data will appear here.'}
          </div>
        )}
      </div>
    </div>
  );
}

function TutorialEditor() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', description: '', visibility: 'public', tags: [] });
  const [tagInput, setTagInput] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { fetchItems(); }, []);
  const fetchItems = () =>
    axios.get(`${API}/admin/tutorials`, { withCredentials: true })
      .then(r => { if (Array.isArray(r.data)) setItems(r.data); setErr(''); })
      .catch(e => {
        if (e.response?.status === 401 || e.response?.status === 403) {
          setErr('Your admin session has expired. Please log out and log back in, then return to this tab.');
        } else {
          setErr(e.response?.data?.detail || 'Failed to load tutorials. Try refreshing the page.');
        }
      });

  const resetForm = () => {
    setForm({ title: '', slug: '', description: '', visibility: 'public', tags: [] });
    setFile(null); setEditing(null); setCreating(false); setErr(''); setTagInput('');
  };

  const startEdit = (t) => {
    setEditing(t.id); setCreating(true); setFile(null); setErr(''); setTagInput('');
    setForm({ title: t.title || '', slug: t.slug || '', description: t.description || '', visibility: t.visibility || 'public', tags: t.tags || [] });
  };

  const addTag = (raw) => {
    const cleaned = (raw || '').trim();
    if (!cleaned || cleaned.length > 30) return;
    if (form.tags.some(t => t.toLowerCase() === cleaned.toLowerCase())) return;
    if (form.tags.length >= 10) return;
    setForm({ ...form, tags: [...form.tags, cleaned] });
  };

  const suggestFromContent = async () => {
    setSuggesting(true); setErr('');
    try {
      const fd = new FormData();
      if (file) fd.append('source_file', file);
      if (form.title) fd.append('text', `${form.title}\n${form.description || ''}`);
      fd.append('count', '8');
      if (!file && !form.title) { setErr('Choose the HTML file first, or enter a title/description to suggest tags from.'); setSuggesting(false); return; }
      const { data } = await axios.post(`${API}/admin/suggest-tags`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      const merged = [...form.tags];
      (data.tags || []).forEach(t => {
        if (!merged.some(x => x.toLowerCase() === t.toLowerCase()) && merged.length < 10) merged.push(t);
      });
      setForm({ ...form, tags: merged });
      setMsg(`Added ${Math.min(data.tags?.length || 0, 10 - form.tags.length)} suggested tags`);
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Tag suggestion failed');
    }
    setSuggesting(false);
  };

  const submit = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      const fd = new FormData();
      fd.append('title', form.title || '');
      fd.append('slug', form.slug || '');
      fd.append('description', form.description || '');
      fd.append('visibility', form.visibility || 'public');
      fd.append('tags', JSON.stringify(form.tags || []));
      if (file) fd.append('html_file', file);

      if (editing) {
        await axios.put(`${API}/admin/tutorials/${editing}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        setMsg('Tutorial updated!');
      } else {
        if (!file) { setErr('Please choose an .html file to upload.'); setSaving(false); return; }
        await axios.post(`${API}/admin/tutorials`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        setMsg('Tutorial uploaded!');
      }
      resetForm();
      fetchItems();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to save tutorial');
    }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/tutorials/${id}`, { withCredentials: true });
      setMsg('Tutorial deleted'); fetchItems();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const visibilityChip = (v) => {
    const cfg = {
      public:  { Icon: Globe, color: '#0077B6', label: 'Public'  },
      partial: { Icon: Eye,   color: '#D97706', label: 'Preview' },
      private: { Icon: Lock,  color: '#CC0000', label: 'Private' },
    }[v] || { Icon: Globe, color: '#0077B6', label: v };
    const Icon = cfg.Icon;
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 font-mono uppercase tracking-wider" style={{ color: cfg.color, borderColor: cfg.color, border: `1px solid ${cfg.color}40`, backgroundColor: `${cfg.color}15` }}>
        <Icon className="w-3 h-3" strokeWidth={2} /> {cfg.label}
      </span>
    );
  };

  return (
    <div data-testid="tutorial-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Interactive Tutorials</h3>
          <p className="text-xs text-zinc-500 mt-1">Self-contained .html files (d3, canvas, multi-tab lessons). Rendered in a sandboxed iframe at <code className="text-zinc-300">/tutorials</code>.</p>
        </div>
        <button onClick={() => { if (creating) resetForm(); else { resetForm(); setCreating(true); } }} className="brand-btn text-sm px-4 py-2 flex items-center gap-2" data-testid="tutorial-new-btn">
          {creating ? <><X className="w-4 h-4" /> Cancel</> : <><Plus className="w-4 h-4" /> New Tutorial</>}
        </button>
      </div>

      {msg && <div className="mb-4 p-3 bg-green-900/20 border border-green-600 text-green-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {msg}</div>}
      {err && <div className="mb-4 p-3 bg-red-900/20 border border-red-600 text-red-400 text-sm" data-testid="tutorial-error-msg">{err}</div>}

      {creating && (
        <div className="border border-[#262626] bg-[#0A0A0A] p-6 mb-6 space-y-4" data-testid="tutorial-form">
          <p className="text-base font-semibold text-white">{editing ? 'Edit Tutorial' : 'New Tutorial'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Title</label>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="e.g., NLP: Topic Modeling" data-testid="tutorial-title-input" />
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Slug (optional)</label>
              <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" placeholder="auto-generated from title" data-testid="tutorial-slug-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6] resize-none" placeholder="Short summary shown on the tutorials catalog card" data-testid="tutorial-desc-input" />
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">Visibility</label>
              <select value={form.visibility} onChange={e => setForm({...form, visibility: e.target.value})} className="w-full px-4 py-3 bg-[#111111] border border-[#262626] text-white outline-none focus:border-[#0077B6]" data-testid="tutorial-visibility-select">
                <option value="public">Public — fully open</option>
                <option value="partial">Partial — first tab public, rest gated</option>
                <option value="private">Private — login required</option>
              </select>
              <p className="text-xs text-zinc-500 mt-1.5">For Partial: visitors see tab 1 freely; tabs 2+ show a 🔒 with a "Sign in" CTA. Single-tab files render fully to public.</p>
            </div>
            <div>
              <label className="text-sm text-[#F59E0B] font-bold block mb-1">{editing ? 'Replace HTML file (optional)' : 'HTML file'}</label>
              <input type="file" accept=".html,.htm" onChange={e => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-zinc-200 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0077B6] file:text-white file:cursor-pointer" data-testid="tutorial-file-input" />
              <p className="text-xs text-zinc-500 mt-1.5">Self-contained .html, max 5MB. Scripts run inside a sandboxed iframe.</p>
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm text-[#F59E0B] font-bold block">Tags (5-10)</label>
                <button
                  type="button"
                  onClick={suggestFromContent}
                  disabled={suggesting}
                  className="text-xs px-3 py-1 border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-black transition-colors disabled:opacity-50 flex items-center gap-1"
                  data-testid="tutorial-suggest-tags-btn"
                  title="Analyse the uploaded HTML + title to suggest topic tags via AI"
                >
                  {suggesting ? 'Analysing…' : '✨ Auto-suggest from file'}
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 p-3 bg-[#111111] border border-[#262626] min-h-[52px]" data-testid="tutorial-tags-box">
                {form.tags.map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/40">
                    {t}
                    <button type="button" onClick={() => setForm({...form, tags: form.tags.filter((_, j) => j !== i)})} className="hover:text-white leading-none text-base -mt-0.5" aria-label={`Remove ${t}`}>×</button>
                  </span>
                ))}
                <input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput); setTagInput(''); } if (e.key === ',' ) { e.preventDefault(); addTag(tagInput); setTagInput(''); } }}
                  onBlur={() => { if (tagInput.trim()) { addTag(tagInput); setTagInput(''); } }}
                  className="flex-1 min-w-[180px] bg-transparent text-sm text-white outline-none"
                  placeholder={form.tags.length >= 10 ? 'Max 10 tags' : 'Type tag and press Enter or comma…'}
                  disabled={form.tags.length >= 10}
                  data-testid="tutorial-tag-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1.5">Tags appear on the public tutorials catalog and help visitors filter by topic. Max 10 tags, 30 chars each.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={submit} disabled={saving || !form.title} className="brand-btn text-sm px-6 py-2 flex items-center gap-2 disabled:opacity-50" data-testid="tutorial-submit-btn">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : editing ? 'Update Tutorial' : 'Upload Tutorial'}
            </button>
            <button onClick={resetForm} className="text-sm text-zinc-400 hover:text-white">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-0 border border-[#262626]" data-testid="tutorial-list">
        {items.map((t, i) => (
          <div key={t.id} className="flex items-center justify-between p-4 border-b border-[#262626] last:border-b-0 bg-[#111111]" data-testid={`tutorial-row-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <p className="text-base font-medium text-white">{t.title}</p>
                {visibilityChip(t.visibility)}
              </div>
              <p className="text-xs text-zinc-500 font-mono">{t.slug} · {Math.round((t.size || 0)/1024)} KB · uploaded {new Date(t.created_at).toLocaleDateString()}</p>
              {t.description && <p className="text-xs text-zinc-400 mt-1 line-clamp-1">{t.description}</p>}
              {t.tags && t.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {t.tags.map((tag, j) => (
                    <span key={j} className="text-xs font-mono px-1.5 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B]">{tag}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 ml-3">
              <a href={`/tutorials/${t.slug}`} target="_blank" rel="noreferrer" className="text-sm text-[#0077B6] border border-[#0077B6] px-3 py-1.5 hover:bg-[#0077B6] hover:text-white transition-colors flex items-center gap-1" data-testid={`tutorial-view-${i}`}>
                <Eye className="w-3.5 h-3.5" /> View
              </a>
              <button onClick={() => startEdit(t)} className="text-sm text-zinc-300 border border-[#262626] px-3 py-1.5 hover:bg-[#262626] transition-colors flex items-center gap-1" data-testid={`tutorial-edit-${i}`}>
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
              <button onClick={() => remove(t.id, t.title)} className="text-sm text-[#F59E0B] border border-[#CC0000] px-3 py-1.5 hover:bg-[#CC0000] hover:text-white transition-colors flex items-center gap-1" data-testid={`tutorial-delete-${i}`}>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-zinc-400" data-testid="tutorial-empty-list">No tutorials yet — upload your first .html file</div>}
      </div>
    </div>
  );
}


export default function ContentManager({ activeSection = 'events' }) {
  const [section, setSection] = useState(activeSection);

  const tabs = [
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'tutorials', label: 'Tutorials', icon: FileCode },
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'partners', label: 'Partners', icon: Settings },
    { id: 'testimonials', label: 'Testimonials', icon: FileText },
    { id: 'team', label: 'Team', icon: Settings },
    { id: 'careers', label: 'Careers', icon: BookOpen },
    { id: 'sessions', label: 'Resume Submissions', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div data-testid="content-manager">
      <div className="flex flex-wrap gap-0 border-b border-[#262626] mb-6">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setSection(t.id)} data-testid={`cm-tab-${t.id}`} className={`px-3 py-2 text-xs font-medium border-b-2 flex items-center gap-1.5 transition-colors ${section === t.id ? 'border-[#CC0000] text-[#F59E0B]' : 'border-transparent text-zinc-400 hover:text-zinc-200'}`}>
            <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.label}
          </button>
        ))}
      </div>
      {section === 'events' && <EventEditor />}
      {section === 'blog' && <BlogEditor />}
      {section === 'tutorials' && <TutorialEditor />}
      {section === 'courses' && <CourseEditor />}
      {section === 'partners' && <EmployerPartnersEditor />}
      {section === 'testimonials' && <TestimonialsEditor />}
      {section === 'team' && <TeamEditor />}
      {section === 'careers' && <IoaCareersEditor />}
      {section === 'sessions' && <CareerSessionsViewer />}
      {section === 'settings' && <SiteSettingsEditor />}
    </div>
  );
}
